// Cross-device sync, layered on top of the existing localStorage behavior
// rather than replacing it. localStorage stays the immediate, always-available
// cache; Firestore is the thing that lets a second device catch up.
//
// IMPORTANT - this file was written without a live Firebase project to test
// against (no network access in the environment that built it). The shape
// of the calls is correct Firebase v9+ modular API, but please test the
// full flow (sign in on device A, check in, open device B, confirm it
// appears) before relying on this with real data.
//
// Sign-in model: everyone starts on anonymous auth automatically - no
// signup needed, sync "just works" across sessions on the same device.
// That alone does NOT sync across different devices/browsers, since each
// gets its own anonymous identity. linkAccount() upgrades the anonymous
// user to an email+password identity (Firebase preserves the same uid and
// all their data when you do this via linkWithCredential) - signing into
// that same email on a second device is what makes sync actually cross
// devices.

import {
  signInAnonymously,
  onAuthStateChanged,
  linkWithCredential,
  EmailAuthProvider,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, isFirebaseConfigured } from '../config/firebaseConfig';

const docRef = (uid) => doc(db, 'users', uid, 'app', 'data');

/**
 * Calls back with the current user's uid whenever auth state is ready,
 * signing in anonymously first if there's no session yet. Calls back with
 * null if Firebase isn't configured, so callers can skip sync entirely.
 */
export const initAuth = (onReady) => {
  if (!isFirebaseConfigured || !auth) {
    onReady(null);
    return () => {};
  }
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      onReady(user.uid);
      return;
    }
    try {
      const cred = await signInAnonymously(auth);
      onReady(cred.user.uid);
    } catch (e) {
      console.error('Anonymous sign-in failed, continuing local-only:', e);
      onReady(null);
    }
  });
  return unsubscribe;
};

/** Upgrades the current anonymous session to an email+password identity,
 * keeping the same uid and all synced data. Use this on device A, then
 * signInWithEmail(...) with the same credentials on device B. */
export const linkEmailToAccount = async (email, password) => {
  if (!auth?.currentUser) throw new Error('No active session to link');
  const credential = EmailAuthProvider.credential(email, password);
  const result = await linkWithCredential(auth.currentUser, credential);
  return result.user.uid;
};

export const signInWithEmail = (email, password) => {
  if (!auth) throw new Error('Firebase not configured');
  return signInWithEmailAndPassword(auth, email, password);
};

/** One-time read, used on connect to reconcile with whatever's already local. */
export const fetchRemoteData = async (uid) => {
  if (!isFirebaseConfigured || !db) return null;
  const snap = await getDoc(docRef(uid));
  return snap.exists() ? snap.data() : null;
};

/** Fire-and-forget write-through. Never throws into the caller - a failed
 * sync write should not interrupt someone using the app offline. */
export const pushToRemote = (uid, { userData, dailyLogs }) => {
  if (!isFirebaseConfigured || !db || !uid) return;
  setDoc(docRef(uid), { userData, dailyLogs, updatedAt: Date.now() }, { merge: false })
    .catch(e => console.error('Sync write failed (data is still saved locally):', e));
};

/**
 * Merges local and remote daily logs (union by date; on the same date,
 * local wins since it reflects the most recent action on this device),
 * then recomputes streak/bestStreak/totalDays from the merged logs so the
 * numbers are always consistent with the actual log history rather than
 * trying to reconcile two independently-tracked counters.
 */
export const mergeAndRecompute = (local, remote) => {
  const mergedLogs = { ...(remote?.dailyLogs || {}), ...(local?.dailyLogs || {}) };
  const stats = computeStatsFromLogs(mergedLogs);
  const name = local?.userData?.name || remote?.userData?.name || '';
  const achievements = local?.userData?.achievements?.length
    ? local.userData.achievements
    : (remote?.userData?.achievements || []);

  return {
    dailyLogs: mergedLogs,
    userData: {
      name,
      achievements,
      lastCheckIn: local?.userData?.lastCheckIn || remote?.userData?.lastCheckIn || null,
      ...stats,
    },
  };
};

/** Derives totalDays / bestStreak / current streak directly from log dates,
 * rather than trusting a separately-tracked counter that could drift after
 * a merge. streak only counts as "current" if the most recent log is today
 * or yesterday - otherwise it's a stale run, not an active streak. */
export function computeStatsFromLogs(logs) {
  const dates = Object.keys(logs).sort();
  let bestStreak = 0;
  let currentRun = 0;
  let prevDate = null;

  for (const dateStr of dates) {
    const isClean = !!logs[dateStr]?.isClean;
    const d = new Date(`${dateStr}T00:00:00`);
    const isConsecutive = prevDate && (d - prevDate) === 86400000;
    currentRun = isClean ? (isConsecutive ? currentRun + 1 : 1) : 0;
    bestStreak = Math.max(bestStreak, currentRun);
    prevDate = d;
  }

  const lastDate = dates[dates.length - 1] || null;
  const todayStr = new Date().toISOString().split('T')[0];
  const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const streakIsCurrent = lastDate === todayStr || lastDate === yesterdayStr;

  return {
    totalDays: dates.length,
    bestStreak,
    streak: streakIsCurrent ? currentRun : 0,
  };
}
